import { cn } from '@/lib/utils'
import { getScoreLevel } from '@limo/shared'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { LeadScoreBreakdown } from '@limo/shared'

interface LeadScoreBadgeProps {
  score: number
  breakdown?: LeadScoreBreakdown | null
  showLabel?: boolean
  size?: 'sm' | 'md'
  className?: string
}

const BREAKDOWN_LABELS: Record<string, string> = {
  has_email: 'Email provided',
  has_phone: 'Phone provided',
  high_value: 'High value quote',
  medium_value: 'Medium value quote',
  value_score: 'Quote value',
  vehicle_selected: 'Vehicle selected',
  urgent_booking: 'Urgent booking (< 7 days)',
  has_date: 'Date provided',
  referral_source: 'Referral source',
  website_source: 'Website source',
}

export function LeadScoreBadge({
  score,
  breakdown,
  showLabel = true,
  size = 'md',
  className,
}: LeadScoreBadgeProps) {
  const level = getScoreLevel(score)

  const badge = (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        level.bgColor,
        level.color,
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        className
      )}
      role="status"
      aria-label={`Lead score: ${score} (${level.label})`}
    >
      <span className="font-bold">{score}</span>
      {showLabel && <span className="opacity-80">{level.label}</span>}
    </div>
  )

  // If no breakdown, just show the badge
  if (!breakdown || Object.keys(breakdown).length === 0) {
    return badge
  }

  // Format breakdown for tooltip
  const breakdownItems = Object.entries(breakdown)
    .filter(([, points]) => points && points > 0)
    .map(([key, points]) => ({
      label: BREAKDOWN_LABELS[key] || key.replace(/_/g, ' '),
      points,
    }))

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">Score Breakdown</p>
            <ul className="space-y-0.5 text-sm">
              {breakdownItems.map((item) => (
                <li key={item.label} className="flex justify-between gap-4">
                  <span>{item.label}</span>
                  <span className="font-medium">+{item.points}</span>
                </li>
              ))}
            </ul>
            <div className="border-t pt-1 mt-2 flex justify-between font-medium">
              <span>Total</span>
              <span>{score}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
